import * as geom from "js/core/utilities/geom";
import { layoutHelper } from "js/elements/layouts/LayoutHelper";
import { VerticalAlignType, HorizontalAlignType, HeaderPositionType, ConnectorType } from "common/constants";
import { calcCircleLayout } from "./CircleLayout";
import { calcArcConnectorLayout, calcStraightConnectorLayout } from "./ConnectorLayout";
import { getSizingValue } from "js/core/utilities/utilities";
import { _ } from "js/vendor";
import { Path } from "js/elements/base/Shapes";

class ElementLayouter {
    get HorizontalAlignType() {
        return {
            LEFT: "left",
            CENTER: "center",
            RIGHT: "right"
        };
    }

    get VerticalAlignType() {
        return {
            TOP: "top",
            MIDDLE: "middle",
            BOTTOM: "bottom"
        };
    }

    get VerticalBlockAlignType() {
        return {
            TOP: "top",
            MIDDLE: "middle",
            BOTTOM: "bottom",
            MIDDLE_OR_TOP: "middle-or-top",
            MIDDLE_TITLE: "center-title"
        };
    }

    get isFit() {
        return this._isFit;
    }

    set isFit(value) {
        this._isFit = value;
        if (this.element) {
            this.element.isFit = value;
        }
    }

    constructor(element, items, styles, containerSize) {
        this.element = element;

        this.items = _.compact(items); // compact to remove any null items (convenient for optional elements)
        this.styles = styles;
        this.containerSize = containerSize;
        this.isFit = true;
    }

    calcHorizontalLayout(options = {}) {
        let styles = _.defaults({}, this.styles, {
            maxColumnWidth: "100%",
            minColumnWidth: "10%",
            hGap: 0
        });

        let maxColumnWidth = getSizingValue(styles.maxColumnWidth, this.containerSize.width) || 200;
        let minColumnWidth = getSizingValue(styles.minColumnWidth, this.containerSize.width) || 100;

        let columnWidth = Math.clamp((this.containerSize.width - styles.hGap * (this.items.length - 1)) / this.items.length, minColumnWidth, maxColumnWidth);

        let itemProps = [];
        let x = 0;
        let totalSize = new geom.Size(-styles.hGap, this.containerSize.height);

        if (options.verticallyAlignText) {
            let maxTextHeight = 0;
            for (let item of this.items) {
                maxTextHeight = Math.max(maxTextHeight, item.text.calcSize(new geom.Size(columnWidth, this.containerSize.height)).height + item.styles.paddingTop + item.styles.marginTop + item.styles.paddingBottom + item.styles.marginBottom);
            }
            maxTextHeight = Math.ceil(maxTextHeight);
            options.itemOptions = Object.assign({ textHeight: maxTextHeight }, options.itemOptions);
        }

        for (let item of this.items) {
            let calculatedSize = item.calcSize(new geom.Size(columnWidth, this.containerSize.height), options.itemOptions);
            item.bounds = new geom.Rect(x, 0, new geom.Size(columnWidth, calculatedSize.height));

            x += columnWidth + styles.hGap;
            totalSize.width += columnWidth + this.styles.hGap;
        }

        let tallestItemHeight = _.maxBy(this.items, item => item.calculatedSize.height).calculatedSize.height;
        totalSize.height = tallestItemHeight;

        switch (options.verticalItemAlign || VerticalAlignType.TOP) {
            case VerticalAlignType.TOP:
                break;
            case VerticalAlignType.MIDDLE:
                for (let item of this.items) {
                    item.bounds.top = tallestItemHeight / 2 - item.calculatedSize.height / 2;
                }
                break;
            case VerticalAlignType.BOTTOM:
                for (let item of this.items) {
                    item.bounds.top = tallestItemHeight - item.calculatedSize.height;
                }
                break;
        }

        this.isFit = totalSize.width <= this.containerSize.width && totalSize.height <= this.containerSize.height;

        if (this.styles.maxItemCount) {
            this.isFit = this.isFit && this.items.length <= this.styles.maxItemCount;
        }

        this.size = totalSize;

        return this;
    }

    calcVerticalLayout(options = {}) {
        let styles = _.defaults({}, this.styles, {
            vGap: 0,
            maxRowHeight: 100,
            minRowHeight: 50,
        });

        let maxRowHeight = getSizingValue(styles.maxRowHeight, this.containerSize.height) || 200;
        let minRowHeight = getSizingValue(styles.minRowHeight, this.containerSize.height) || 100;

        let rowHeight = Math.clamp((this.containerSize.height - styles.vGap * (this.items.length - 1)) / this.items.length, minRowHeight, maxRowHeight);

        let y = 0;
        let totalSize = new geom.Size(this.containerSize.width, -styles.vGap);
        for (let item of this.items) {
            let calculatedSize = item.calcSize(new geom.Size(this.containerSize.width, rowHeight), options.itemOptions);
            item.bounds = new geom.Rect(0, y, new geom.Size(this.containerSize.width, rowHeight));

            y += rowHeight + styles.vGap;
            totalSize.height += rowHeight + styles.vGap;
        }

        this.isFit = totalSize.width <= this.containerSize.width && totalSize.height <= this.containerSize.height;

        if (this.styles.maxItemCount) {
            this.isFit = this.items.length <= this.styles.maxItemCount;
        }

        this.size = totalSize;

        return this;
    }

    alignHorizontally(align) {
        if (this.isFit === false) {
            return this;
        }
        if (!this.size) {
            this.size = this.items[0].calculatedSize;
        }

        let offsetX;
        switch (align || this.styles.hGridAlign || this.styles.horizontalAlign || "center") {
            case this.HorizontalAlignType.LEFT:
                offsetX = 0;
                break;
            case this.HorizontalAlignType.CENTER:
                offsetX = this.containerSize.width / 2 - this.size.width / 2;
                break;
            case this.HorizontalAlignType.RIGHT:
                offsetX = this.containerSize.width - this.size.width;
                break;
        }

        for (let item of this.items) {
            item.bounds = item.bounds.offset(offsetX, 0);
        }
        return this;
    }

    alignVertically(align, adjustOptical = true) {
        if (this.isFit === false) {
            return this;
        }
        if (!this.size) {
            this.size = this.items[0].calculatedSize;
        }

        let offsetY;
        switch (align || "middle") {
            case this.VerticalBlockAlignType.TOP:
                offsetY = 0;
                break;
            case this.VerticalBlockAlignType.MIDDLE:
                offsetY = this.containerSize.height / 2 - this.size.height / 2;

                // adjust for optical white space
                if (adjustOptical && this.element.canvas.model.layout.headerPosition === HeaderPositionType.TOP) {
                    offsetY -= Math.max(this.containerSize.height * 0.75 - this.size.height, 0) * 0.1;
                }
                break;
            case this.VerticalBlockAlignType.BOTTOM:
                offsetY = this.containerSize.height - this.size.height;
                break;
        }

        for (let item of this.items) {
            item.bounds = item.bounds.offset(0, offsetY);
        }
        return this;
    }

    distributeHorizontally(options) {
        if (!this.items.length) {
            this.size = this.containerSize;
            return this;
        }

        options = _.defaults(options, {
            verticalAlign: VerticalAlignType.TOP,
            gap: 0,
            reverse: false,
            useCalculatedSizes: false,
            itemOptions: {}
        });

        let totalWidth = -options.gap;
        let availableSize = this.containerSize.clone();

        if (options.reserveMinWidths) {
            let widths = this.items.reduce((acc, item) => acc + item.minWidth + options.gap, -options.gap);
            availableSize = availableSize.deflate({ right: widths });
        }

        if (availableSize.width < 0) {
            this.isFit = false;
        }

        for (let item of this.items) {
            if (options.reserveMinWidths) {
                availableSize = availableSize.inflate({ right: item.minWidth });
            }
            let itemSize = (options.useCalculatedSizes && item.calculatedSize) ? item.calculatedSize : item.calcSize(availableSize, options.itemOptions);
            availableSize = availableSize.deflate({ right: itemSize.width });
            totalWidth += itemSize.width + options.gap;
        }
        if (totalWidth > this.containerSize.width) {
            this.isFit = false;
        }

        let maxHeight = _.maxBy(this.items, item => item.calculatedSize.height).calculatedSize.height;
        let x = 0;

        let items = options.reverse ? _.reverse(this.items) : this.items;
        for (let item of items) {
            item.bounds = new geom.Rect(x, layoutHelper.getVerticalAlignOffset(item.calculatedSize.height, maxHeight, options.verticalAlign), item.calculatedSize);
            x += item.calculatedSize.width + options.gap;
        }

        this.size = new geom.Size(x - options.gap, maxHeight);
        return this;
    }

    distributeVertically(options) {
        if (!this.items.length) {
            this.size = this.containerSize;
            return this;
        }

        options = _.defaults(options, {
            horizontalAlign: HorizontalAlignType.CENTER,
            gap: 0,
            reverse: false,
            useCalculatedSizes: false,
            itemOptions: {}
        });

        let totalHeight = -options.gap;
        let availableSize = this.containerSize.clone();

        if (options.reserveMinHeights) {
            let heights = this.items.reduce((acc, item) => acc + item.minHeight + options.gap, -options.gap);
            availableSize = availableSize.deflate({ bottom: heights });
        }
        for (let item of this.items) {
            if (options.reserveMinHeights) {
                availableSize = availableSize.inflate({ bottom: item.minHeight });
            }
            let itemSize = (options.useCalculatedSizes && item.calculatedSize) ? item.calculatedSize : item.calcSize(availableSize, options.itemOptions);
            availableSize = availableSize.deflate({ top: itemSize.height });
            totalHeight += itemSize.height + options.gap;
        }
        if (totalHeight > this.containerSize.height) {
            this.isFit = false;
        }

        let maxWidth = _.maxBy(this.items, item => item.calculatedSize.width).calculatedSize.width;

        let y = 0;
        for (let item of this.items) {
            item.bounds = new geom.Rect(layoutHelper.getHorizontalAlignOffset(item.calculatedSize.width, maxWidth, options.horizontalAlign), y, item.calculatedSize);
            y += item.calculatedSize.height + options.gap;
        }

        this.size = new geom.Size(maxWidth, y - options.gap);
        return this;
    }

    calcCircleLayout(options) {
        this.size = this.containerSize;

        let { shape, angles, points, itemSizes, correctedCenter } = calcCircleLayout(Object.assign({
            size: this.size,
            styles: this.styles,
            items: this.items
        }, options));
        this.shape = shape;
        this.angles = angles;
        this.itemSizes = itemSizes;
        this.points = points;
        this.correctedCenter = correctedCenter;

        if (this.styles.maxItemCount) {
            this.isFit = this.items.length <= this.styles.maxItemCount;
        }

        this.items.map((item, index) => {
            let angle = this.angles[index];
            let itemSize = this.itemSizes[index];
            let point = this.shape.point(angle);
            // let position = item.contentBounds.center().delta(point);
            // let position = item.centerPoint.delta(point);
            item.bounds = new geom.Rect(point.x, point.y, itemSize.width, itemSize.height);
        });
        return this;
    }

    calcConnectorLayout(options) {
        let connectorLayoutFn;
        if (options.connectorType === ConnectorType.ARC) {
            connectorLayoutFn = calcArcConnectorLayout;
        } else if (options.connectorType === ConnectorType.STRAIGHT) {
            connectorLayoutFn = calcStraightConnectorLayout;
        }
        this.items.map((item, index) => {
            connectorLayoutFn({
                index,
                item,
                shape: this.shape,
                angles: this.angles,
                items: this.items,
                styles: this.styles,
                points: this.points,
                correctedCenter: this.correctedCenter,
                content_size: options.content_size,
                show_arrows: options.show_arrows,
                pointing_in: options.pointing_in,
                start_angle: options.start_angle,
                circle_radius: options.circle_radius
            });

            this.isFit = this.isFit && item.connectorFits;

            if (this.isFit) {
                let { linePath, arrowPath } = item.calcConnector();
                item.line.styles = this.styles.arrowArcs;
                item.line.path = linePath;

                if (options.show_arrows) {
                    item.arrow.styles = this.styles.arrowHeads;
                    item.arrow.path = arrowPath;
                }
            }
        });

        return this;
    }
}

export { ElementLayouter };
